import React, {useState, useEffect} from "react";
import {api} from "./api";

const REVIEWER_STORAGE = "orion_picker_reviewer";

export interface Reviewer {
  email: string;
  name: string;
  registered_at: string;
  status?: ReviewerStatus;
}

export type ReviewerStatus =
  | "unregistered" | "pending" | "approved" | "rejected";

export function getReviewer(): Reviewer | null {
  try {
    const raw = localStorage.getItem(REVIEWER_STORAGE);
    if (!raw) return null;
    const r = JSON.parse(raw);
    if (r.email && r.name) return r;
    return null;
  } catch { return null; }
}
export function setReviewer(r: Reviewer): void {
  localStorage.setItem(REVIEWER_STORAGE, JSON.stringify(r));
}
export function clearReviewer(): void {
  localStorage.removeItem(REVIEWER_STORAGE);
}

export async function fetchStatus(email: string): Promise<ReviewerStatus> {
  try {
    const r = await api(`/me?email=${encodeURIComponent(email)}`);
    return (r?.status as ReviewerStatus) || "unregistered";
  } catch {
    return "unregistered";
  }
}

export function Registration({onApproved}: {onApproved: () => void}) {
  const existing = getReviewer();
  const [email, setEmail] = useState(existing?.email || "");
  const [name, setName]   = useState(existing?.name  || "");
  const [err, setErr]     = useState("");
  const [phase, setPhase] = useState<"form" | "submitting" | "pending" | "rejected">(
      existing?.status === "pending" ? "pending"
    : existing?.status === "rejected" ? "rejected"
    : "form");
  const [pollMsg, setPollMsg] = useState("");

  async function submit() {
    const e = email.trim().toLowerCase();
    const n = name.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { setErr("Please enter a real email address."); return; }
    if (n.length < 2)                              { setErr("Please enter your name.");          return; }
    setErr(""); setPhase("submitting");
    try {
      const r = await api("/register", {method: "POST", body: JSON.stringify({email: e, name: n})});
      const status = (r?.status as ReviewerStatus) || "pending";
      setReviewer({email: e, name: n, registered_at: new Date().toISOString(), status});
      if (status === "approved") { onApproved(); return; }
      if (status === "rejected") { setPhase("rejected"); return; }
      setPhase("pending");
    } catch (ex: any) {
      setErr("Registration failed: " + (ex?.message || ex));
      setPhase("form");
    }
  }

  // While pending, poll /me every 15 s for status changes
  useEffect(() => {
    if (phase !== "pending") return;
    const e = (existing?.email || email).trim().toLowerCase();
    let cancelled = false;
    let tick = 0;
    const id = setInterval(async () => {
      if (cancelled) return;
      tick++;
      setPollMsg(`checked ${tick}× …`);
      const status = await fetchStatus(e);
      if (cancelled) return;
      if (status === "approved") {
        const r = getReviewer();
        if (r) setReviewer({...r, status});
        onApproved();
      } else if (status === "rejected") {
        setPhase("rejected");
      }
    }, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [phase, email, existing?.email, onApproved]);

  // Common gate shell
  const wrap = (children: React.ReactNode) =>
      <div className="gate">{children}</div>;

  if (phase === "submitting") return wrap(<p>Submitting…</p>);

  if (phase === "pending") return wrap(<>
    <h2 style={{marginTop: 0}}>Pending approval</h2>
    <p style={{color: "#cbd2e0"}}>
      Thanks — your registration is in. Jonah has to approve
      <strong> {existing?.email || email} </strong> before you can save
      picks. This page will refresh automatically once approved.
    </p>
    <p style={{fontSize: 12, color: "#9ba2b3"}}>{pollMsg}</p>
    <button className="btn" style={{marginTop: 8}}
            onClick={() => { clearReviewer(); setPhase("form"); }}>
      Use a different email
    </button>
  </>);

  if (phase === "rejected") return wrap(<>
    <h2 style={{marginTop: 0, color: "#fca5a5"}}>Access denied</h2>
    <p style={{color: "#cbd2e0"}}>
      <strong>{existing?.email || email}</strong> is not authorized to use
      this picker. Contact Jonah if you think this is a mistake.
    </p>
    <button className="btn" style={{marginTop: 8}}
            onClick={() => { clearReviewer(); setPhase("form"); }}>
      Use a different email
    </button>
  </>);

  // form
  return wrap(<>
    <h2 style={{marginTop: 0}}>Register to access the picker</h2>
    <p style={{color: "#9ba2b3", fontSize: 13, marginTop: 0}}>
      Access is restricted. Submit your name + email; Jonah approves
      each reviewer individually. Your address stays in your browser
      and on the project server.
    </p>
    <label style={{fontSize: 12, color: "#cbd2e0"}}>Name</label>
    <input value={name} placeholder="e.g. Jane Doe"
           onChange={e => setName(e.target.value)}
           style={inputStyle} autoFocus />
    <label style={{fontSize: 12, color: "#cbd2e0"}}>Email</label>
    <input type="email" value={email} placeholder="jane@example.org"
           onChange={e => setEmail(e.target.value)}
           onKeyDown={e => e.key === "Enter" && submit()}
           style={inputStyle} />
    {err && <p style={{color: "#fca5a5", fontSize: 12, marginTop: -6}}>{err}</p>}
    <button className="btn primary" onClick={submit}>Register</button>
  </>);
}

const inputStyle: React.CSSProperties = {
  width: "100%", padding: 8, margin: "4px 0 14px",
  background: "#0f1115", color: "#e6e9ef",
  border: "1px solid #2a3045", borderRadius: 5, fontSize: 14,
};
